import { useGetGeneralField } from "@src/hooks/ab-testing/use-get-general-field";
import { ApplyValueStateType } from ".";
import { FormInstance } from "antd/lib/form";
import { useLoadAbExploration } from "@src/hooks/ab-testing/use-ab-exploration-get-result";
import {
    ABExplorationKeyWords,
    FilterOperator,
    SupportedModelAlias,
} from "@src/constant/ab-testing/ab-testing-exploration.enum";
import { IQueryFilter } from "@src/types/ab-testing-exploration";
import {
    getListEventNameByOrder,
    getStepByMode,
    getStepByModeWithSampleSize,
    sortListEventNameByName,
} from "@src/util/ab-testing/funnel/manage-state-event-lists";
import React from "react";
import { FunnelTable } from "./funnel-table";
import { ChartWrapperContextProvider, useChartWrapperContext } from "@src/contexts/ab-testing/chart-wrapper-context";
import { DataMode, MappingFunnelDataSource } from "@src/util/ab-testing/funnel/maping-funnel-data-source";
import { Form, Radio, Skeleton } from "antd";
import { FunnelShowMore } from "./funnel-show-more";
import { css } from "@emotion/react";
import { ChartFormFieldsEnum } from "@src/pages/ab-test-explore/enum";
import {
    AbExplorationEvaluateMetricProps,
    useAbExplorationEvaluateMetric,
} from "@src/hooks/ab-testing/explore-evaluate";
import { MappingMetricEvaluate } from "@src/util/ab-testing/funnel/mapping-metric-evaluate";
import { config } from "@src/config";
import { LoadingComponent } from "@src/components/loading-custom";
import { AbLoadingTips, LoadingMessage } from "@src/constant/loading-message";

const FunnelChartContainerWrapper: React.FC<{
    containerWidth: number;
    chartWidth: number;
    applyValue: ApplyValueStateType;
    form: FormInstance;
    setForceResetTable: React.Dispatch<React.SetStateAction<boolean>>;
    forceResetTable: boolean;
}> = ({ form, containerWidth, chartWidth, applyValue, setForceResetTable, forceResetTable }) => {
    return (
        <ChartWrapperContextProvider>
            <FunnelChartContainer
                form={form}
                containerWidth={containerWidth}
                chartWidth={chartWidth}
                applyValue={applyValue}
                setForceResetTable={setForceResetTable}
                forceResetTable={forceResetTable}
            />
        </ChartWrapperContextProvider>
    );
};
export const FunnelChartContainerWrapperMemo = React.memo(FunnelChartContainerWrapper);

const FunnelChartContainer: React.FC<{
    containerWidth: number;
    chartWidth: number;
    applyValue: ApplyValueStateType;
    form: FormInstance;
    setForceResetTable: React.Dispatch<React.SetStateAction<boolean>>;
    forceResetTable: boolean;
}> = ({ form, containerWidth, chartWidth, applyValue, setForceResetTable, forceResetTable }) => {
    const previousMetricMeasure = React.useRef<any>(null);
    const [mode, setMode] = React.useState<DataMode>("compareWithUserGroup");
    const { variants, getExpAliasById } = useGetGeneralField({
        form,
    });

    const [significantInfoAllStep, setSignificantInfoAllStep] = React.useState<Record<string, Record<string, any>>>();

    const { productCode, explorationFilters, explorationOptions, maxDayDiff, timeRange } = useGetGeneralField({
        form,
    });
    const metricsInfo = React.useCallback(
        (_mode: DataMode) => {
            const eventNameOrder = getListEventNameByOrder(applyValue.eventLists ?? []);
            return eventNameOrder
                .map((event) =>
                    getStepByModeWithSampleSize(
                        event.name,
                        _mode,
                        eventNameOrder.map((item) => item.name),
                    ),
                )
                .flat(1)
                .filter((item, index, self) => index === self.findIndex((_s) => _s === item));
        },
        [applyValue.eventLists],
    );

    const detailQuery = React.useMemo(() => {
        return {
            dimensions: ["exp_group"].concat(applyValue.breakdownBy ?? []),
            measures: ["user_count", ...metricsInfo(mode)],
            limit: 10000,
            order: [["user_count", "desc"]],
            filters: [
                ...explorationFilters,
                {
                    member: "event_name",
                    operator: FilterOperator.EQUALS,
                    values: sortListEventNameByName(applyValue.eventLists ?? []).map((event) => event.name),
                },
                {
                    member: ABExplorationKeyWords.ACT_DATE,
                    operator: FilterOperator.BETWEEN,
                    values: timeRange?.map((v) => v.format(config.DATE_FORMAT)) ?? [],
                },
                {
                    member: ABExplorationKeyWords.DAY_DIFF,
                    operator: FilterOperator.BETWEEN,
                    values: [0, maxDayDiff],
                },
            ]
                .concat(
                    applyValue.breakdownValues && applyValue.breakdownBy
                        ? [
                              {
                                  member: applyValue.breakdownBy,
                                  operator: FilterOperator.EQUALS,
                                  values: applyValue.breakdownValues ?? [],
                              },
                          ]
                        : [],
                )
                .concat(
                    (applyValue.filters ?? []).map(({ field, operator, value }) => {
                        return {
                            member: field,
                            operator: (operator === "=" ? "equals" : operator) as FilterOperator,
                            values: typeof value === "string" ? [value] : value,
                        };
                    }),
                ) as IQueryFilter[],
        };
    }, [
        applyValue.breakdownBy,
        applyValue.eventLists,
        applyValue.timeRange,
        applyValue.breakdownValues,
        metricsInfo,
        mode,
        JSON.stringify(explorationFilters),
        maxDayDiff,
        timeRange,
        applyValue.filters,
    ]);

    const { result, isLoading, elapsed, meta, renderedQuery } = useLoadAbExploration({
        productCode: productCode,
        modelAlias: SupportedModelAlias.FIREBASE,
        query: detailQuery,
        options: explorationOptions,
        isSkip: !productCode || !applyValue.eventLists || applyValue.eventLists?.length === 0 || !timeRange,
        refineOptions: {
            mutationOptions: {
                onSettled: () => {
                    setForceResetTable(false);
                    previousMetricMeasure.current = metricsInfo(mode);
                },
            },
        },
    });

    const { updateChartState } = useChartWrapperContext();
    React.useEffect(() => {
        updateChartState({
            chartKey: "funnel-table",
            dataSource: result,
            modelAlias: "firebase",
            queryOption: explorationOptions,
            jsonQuery: detailQuery,
            renderedQuery,
            meta,
            elapsed,
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [JSON.stringify({ result, explorationOptions, detailQuery })]);

    const mappingFunnelDataSource = React.useMemo(() => {
        return new MappingFunnelDataSource({
            eventLists: applyValue.eventLists ?? [],
            breakdownValues: applyValue.breakdownValues ?? [],
            breakdown: applyValue.breakdownBy,
            variants: variants ?? [],
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [applyValue.breakdownBy, applyValue.breakdownValues, applyValue.eventLists, JSON.stringify(variants)]);

    mappingFunnelDataSource.setLoading(isLoading);

    return (
        <div
            css={css`
                td.ant-table-row-expand-icon-cell {
                    background-color: #e9f6fe !important;
                }
                td.ant-table-cell-fix-left {
                    &:has(.table-group-custom) {
                        background-color: #e9f6fe !important;
                    }
                }
            `}
        >
            <div className="flex justify-between">
                <Radio.Group
                    className="mb-4"
                    options={[
                        {
                            label: "Cohort user conversion",
                            value: "compareWithUserGroup",
                            disabled: isLoading && mode !== "compareWithUserGroup",
                        },
                        {
                            label: "Compare with first step",
                            value: "compareFirstStep",
                            disabled: isLoading && mode !== "compareFirstStep",
                        },
                        {
                            label: "Compare with previous step",
                            value: "comparePreviousStep",
                            disabled: isLoading && mode !== "comparePreviousStep",
                        },
                    ]}
                    onChange={(e) => {
                        const _mode = e.target.value;
                        setMode(_mode);
                        const newMetrics = metricsInfo(_mode);
                        const previousMetrics = previousMetricMeasure.current;
                        const isMetricChange = JSON.stringify(newMetrics) !== JSON.stringify(previousMetrics);

                        if (isMetricChange) {
                            setForceResetTable(true);
                        }
                    }}
                    value={mode}
                    optionType="button"
                    buttonStyle="solid"
                />
                <FunnelShowMore form={form} />
            </div>
            {forceResetTable ? (
                <LoadingComponent message={LoadingMessage} tips={AbLoadingTips} />
            ) : (
                <FunnelTable
                    applyValue={applyValue}
                    form={form}
                    isLoading={isLoading}
                    mappingFunnelDataSource={mappingFunnelDataSource}
                    mode={mode}
                    result={result}
                    significantInfoAllStep={significantInfoAllStep}
                    variants={variants ?? []}
                    getExpAliasById={getExpAliasById}
                />
            )}

            {result && result.length > 0 && !forceResetTable && mode === "compareWithUserGroup" && (
                <FunnelBatchContainer
                    form={form}
                    mode={mode}
                    applyValue={applyValue}
                    result={result}
                    variants={variants ?? []}
                    setSignificantInfoAllStep={setSignificantInfoAllStep}
                />
            )}
        </div>
    );
};

const FunnelBatchContainerComponent: React.FC<{
    form: FormInstance;
    mode: DataMode;
    applyValue: ApplyValueStateType;
    result: Record<string, any>[];
    variants: string[];
    setSignificantInfoAllStep: React.Dispatch<React.SetStateAction<Record<string, Record<string, any>> | undefined>>;
}> = ({ form, mode, applyValue, result, variants, setSignificantInfoAllStep }) => {
    const stepList = applyValue.eventLists ?? [];

    const [collectBatchResult, setCollectBatchResult] = React.useState(
        stepList.reduce((acc, curr) => {
            return {
                ...acc,
                [curr.name]: {},
            };
        }, {} as Record<string, Record<string, any>>),
    );

    React.useEffect(() => {
        if (Object.values(collectBatchResult).every((item) => JSON.stringify(item) !== JSON.stringify({}))) {
            setSignificantInfoAllStep(collectBatchResult);
        }
    }, [collectBatchResult, setSignificantInfoAllStep]);

    const stepListOrder = getListEventNameByOrder(stepList);
    return (
        <>
            {stepListOrder.map((step) => (
                <FunnelBatchProcess
                    step={step.name}
                    applyValue={applyValue}
                    form={form}
                    result={result}
                    variants={variants}
                    setCollectBatchResult={setCollectBatchResult}
                    metricName={getStepByMode(
                        step.name,
                        mode,
                        stepListOrder.map((item) => item.name),
                    )}
                />
            ))}
        </>
    );
};
export const FunnelBatchContainer = React.memo(
    FunnelBatchContainerComponent,
    (prevProps, nextProps) =>
        JSON.stringify({
            applyValue: prevProps.applyValue,
            result: prevProps.result,
            variants: prevProps.variants,
        }) ===
        JSON.stringify({
            applyValue: nextProps.applyValue,
            result: nextProps.result,
            variants: nextProps.variants,
        }),
);
const FunnelBatchProcessComponent: React.FC<{
    metricName: string;
    step: string;
    form: FormInstance;
    applyValue: ApplyValueStateType;
    result: Record<string, any>[];
    variants: string[];
    setCollectBatchResult: React.Dispatch<React.SetStateAction<Record<string, Record<string, any>>>>;
}> = ({ metricName, step, form, applyValue, result, variants, setCollectBatchResult }) => {
    const cacheRequestPayload = React.useRef<AbExplorationEvaluateMetricProps>();
    const signigicantLevel = Form.useWatch(ChartFormFieldsEnum.FUNNEL_SIGNIFICANT, form);
    const { baseVariant } = useGetGeneralField({ form });

    const mappingMetricEvaluate = new MappingMetricEvaluate({
        significanceLevel: signigicantLevel,
        breakdownBy: applyValue.breakdownBy ? [applyValue.breakdownBy] : null,
        baseVariant: baseVariant,
        variants: variants ?? [],
        measureValue: metricName,
    });

    mappingMetricEvaluate.setLoadResult(result);

    const requestPayload = mappingMetricEvaluate.getMetricEvaluateRequest();

    const isCached = JSON.stringify(requestPayload) === JSON.stringify(cacheRequestPayload.current);
    const { isLoading: isEvaluatingMetric, result: metricEvaluationResult } = useAbExplorationEvaluateMetric({
        ...requestPayload,
        isSkip: isCached || requestPayload.data?.length === 0,
    });

    React.useEffect(() => {
        cacheRequestPayload.current = requestPayload;
    }, [JSON.stringify(requestPayload)]);

    React.useEffect(() => {
        const data = metricEvaluationResult ? mappingMetricEvaluate.getDataSource(metricEvaluationResult) : null;

        if (!data) return;
        setCollectBatchResult((prev) => ({
            ...prev,
            [step]: {
                ...data.reduce((acc, curr) => {
                    return {
                        ...acc,
                        [curr.batchId]: curr,
                    };
                }, {} as Record<string, any>),
            },
        }));
    }, [JSON.stringify(metricEvaluationResult)]);

    return null;
};

const FunnelBatchProcess = React.memo(
    FunnelBatchProcessComponent,
    (prevProps, nextProps) =>
        JSON.stringify({
            applyValue: prevProps.applyValue,
            result: prevProps.result,
            metricName: prevProps.metricName,
            step: prevProps.step,
            variants: prevProps.variants,
        }) ===
        JSON.stringify({
            applyValue: nextProps.applyValue,
            result: nextProps.result,
            metricName: nextProps.metricName,
            step: nextProps.step,
            variants: nextProps.variants,
        }),
);
